import type { ConversationMessage } from '../../types/conversation-message';
import { cn } from '@/utils/cn';

function normalizedText(value: string | null | undefined) {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

function initialsForName(name: string) {
  const words = name
    .split(/\s+/)
    .map(part => part.trim())
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export function MessageIdentityAvatar({
  name,
  avatarURL,
  className,
}: {
  name: string;
  avatarURL?: string | null;
  className?: string;
}) {
  const normalizedAvatarURL = normalizedText(avatarURL);
  if (normalizedAvatarURL) {
    return (
      <img
        src={normalizedAvatarURL}
        alt=""
        className={cn(
          'size-[1.125rem] shrink-0 rounded-full border border-black/[0.07] bg-white object-cover shadow-[0_0.09375rem_0.140625rem_rgba(0,0,0,0.08)]',
          className
        )}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <span
      className={cn(
        'flex size-[1.125rem] shrink-0 items-center justify-center rounded-full border border-black/[0.07] bg-muted text-[0.5rem] font-semibold leading-none text-muted-foreground shadow-[0_0.09375rem_0.140625rem_rgba(0,0,0,0.06)]',
        className
      )}
      aria-hidden="true"
    >
      {initialsForName(name)}
    </span>
  );
}

export function MessageIdentityHeader({
  message,
  align = 'left',
}: {
  message: ConversationMessage;
  align?: 'left' | 'right';
}) {
  const name =
    normalizedText(message.senderName) ??
    normalizedText(message.sourceAgentName);
  if (!name) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 text-[0.6875rem] font-medium leading-4 text-muted-foreground',
        align === 'right' && 'justify-end'
      )}
    >
      {align === 'left' ? (
        <MessageIdentityAvatar
          name={name}
          avatarURL={message.senderAvatarURL}
        />
      ) : null}
      <span className="min-w-0 max-w-[14rem] truncate">{name}</span>
      {align === 'right' ? (
        <MessageIdentityAvatar
          name={name}
          avatarURL={message.senderAvatarURL}
        />
      ) : null}
    </div>
  );
}
